import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@eigtech/ui/components/select'
import { ReactNode, useMemo } from 'react'
import { FieldValues, useController } from 'react-hook-form'
import { WithFieldNameAndDefaultValueProps } from '../types/FieldProps'
import { BaseProps, FieldControl } from './FieldControl'

type Props<TFieldValues extends FieldValues = FieldValues> = {
  selectProps?: React.ComponentProps<typeof Select>
  selectTriggerProps?: React.ComponentProps<typeof SelectTrigger>
  placeholder?: string | false
  children?: ReactNode
  options: { value: string; label?: string }[] | string[]
} & BaseProps<TFieldValues>

export type SelectFieldProps<TFieldValues extends FieldValues = FieldValues> =
  WithFieldNameAndDefaultValueProps<Props<TFieldValues>, TFieldValues>

export function SelectField<TFieldValues extends FieldValues = FieldValues>({
  name,
  control,
  selectProps,
  selectTriggerProps,
  defaultValue,
  children,
  options,
  placeholder,
  ...props
}: SelectFieldProps<TFieldValues>) {
  const items = useMemo(
    () =>
      (options ?? []).map((option: Props['options'][number]) =>
        typeof option === 'string'
          ? { label: option, value: option }
          : { label: option.label || option.value, value: option.value }
      ),
    [options]
  )

  const {
    field,
    formState: { isSubmitting },
  } = useController({
    name,
    control,
    defaultValue,
  })

  return (
    <FieldControl control={control} disabled={isSubmitting} name={name} {...props}>
      <Select {...field} id={name} items={items} onValueChange={field.onChange} {...selectProps}>
        <SelectTrigger {...selectTriggerProps}>
          <SelectValue
            placeholder={placeholder === false ? undefined : (placeholder ?? 'Select an option')}
          />
        </SelectTrigger>

        <SelectContent positionerClassName="z-50">
          {children ??
            items.map((item) => (
              <SelectItem key={item.value} value={item.value}>
                {item.label}
              </SelectItem>
            ))}
        </SelectContent>
      </Select>
    </FieldControl>
  )
}

export type SelectFieldComponent<TFieldValues extends FieldValues = FieldValues> =
  typeof SelectField<TFieldValues>
